import {
  EMPTY,
  Observable,
  of,
  throwError as observableThrowError,
} from 'rxjs';
import { Location } from '@angular/common';
import { List } from 'immutable';
import { extend } from 'lodash';
import { serialize } from '../../utils/helpers/model.helper';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
} from '../auth-data/auth-http.service';
import { ErrorService } from '../error-reporting/error.service';
import { IResponseInfo } from '../http/http.data';
import { GlobalSearch } from '../search-data/global-search.model';
import { Highlight } from '../search-data/sub-models/highlight.model';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { Settings } from '../settings-data/settings.model';
import { LoggerService } from '../shared/services/logger/logger.service';
import { IEntityDataBuilder } from './entity-data-builder.interface';
import {
  FILTER_ACCEPT_VERSION,
  IEntityData,
  IEntityFilters,
  INPUT_ACCEPT_VERSION,
  INPUT_SEARCH_ACCEPT_VERSION,
  ISearchParams,
  ITopLevelKeys,
  NEXT_PAGE_HEADER,
  SearchParamsEnum,
} from './entity-data.data';
import { IS_NEW_KEY } from './entity.model';
import { GLOBAL_SEARCH_ACCEPT_VERSION } from '../search-data/search-data.service';
import { catchError, map, share, take, withLatestFrom } from 'rxjs/operators';

// should only be used as a superclass; this class is not injectable
export class EntityDataService {
  protected inputHeaders: any = {
    [ACCEPT_VERSION_HEADER]: INPUT_ACCEPT_VERSION, // documents should override this to DOC_ACCEPT_VERSION
  };
  protected searchHeaders: any = {
    [ACCEPT_VERSION_HEADER]: INPUT_SEARCH_ACCEPT_VERSION,
  };
  protected filtersHeaders: any = {
    [ACCEPT_VERSION_HEADER]: FILTER_ACCEPT_VERSION,
  };
  protected topLevelKeys: ITopLevelKeys;
  protected model: any;

  private _searchEntitiesUrl: string;
  private _globalSearchUrl = '/search';
  private _globalSearchHeaders: any = {
    [ACCEPT_VERSION_HEADER]: GLOBAL_SEARCH_ACCEPT_VERSION,
  };

  constructor(
    protected authHttpService: AuthHttpService,
    protected entitiesUrl: string,
    protected loggerService: LoggerService,
    protected errorService: ErrorService,
    protected dataBuilder: IEntityDataBuilder,
    protected settingsDataStore: SettingsDataStore
  ) {
    this.model = dataBuilder.model;
    this._searchEntitiesUrl = Location.joinWithSlash(
      this._globalSearchUrl,
      this.entitiesUrl
    );
  }

  searchEntities(
    searchParams: ISearchParams,
    globalSearch?: boolean
  ): Observable<IEntityData> {
    let queryParams: any = searchParams;

    if (globalSearch) {
      extend(
        queryParams,
        { entity_type: this.model.ENTITY_TYPE },
        searchParams
      );
    }

    const url = globalSearch ? this._globalSearchUrl : this._searchEntitiesUrl;
    const headers = globalSearch
      ? this._globalSearchHeaders
      : this.searchHeaders;

    return this.authHttpService.get(url, queryParams, headers).pipe(
      withLatestFrom(this.settingsDataStore.settings$),
      map(
        ([{ body, headers }, settings]: [
          IResponseInfo,
          Settings
        ]): IEntityData => {
          let globalSearchResults;

          if (globalSearch) {
            globalSearchResults = new GlobalSearch(body).get(
              this.topLevelKeys.globalSearchResultsKey
            );
          }

          const nextPageHeader = headers.get(NEXT_PAGE_HEADER);
          const searchResultParams = nextPageHeader
            ? extend({}, searchParams, { next_page_url: nextPageHeader })
            : searchParams;
          const entities = globalSearchResults
            ? globalSearchResults.results
            : body[this.topLevelKeys.searchResultsKey];
          const highlights = globalSearchResults
            ? globalSearchResults.highlights
            : List(body[SearchParamsEnum.highlights]).map(
                (highlightParams) => new Highlight(highlightParams)
              );

          return {
            entities: [
              List<any>(
                (entities || []).map((value) =>
                  this.dataBuilder.buildEntity(value, settings)
                )
              ),
            ],
            highlights,
            headers,
            searchParams: searchResultParams,
          };
        }
      ),
      take(1),
      share()
    );
  }

  getFilters(listFilter?: string): Observable<IEntityFilters> {
    return EMPTY;
  }

  // TODO [refactor]: throw error rather than null after all stores have been refactored
  getEntity(id: string): Observable<any> {
    if (
      new RegExp(
        /^[0-9A-F]{8}-[0-9A-F]{4}-[4][0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12}$/i
      ).test(id)
    ) {
      const url = Location.joinWithSlash(this.entitiesUrl, id);

      return this.authHttpService.get(url, null, this.inputHeaders).pipe(
        map(
          ({ body, ..._ }: IResponseInfo) =>
            new this.model(body[this.topLevelKeys.getSingleKey])
        ),
        catchError((error) => observableThrowError(error)),
        take(1)
      );
    } else {
      this.errorService.alertSystem(
        new Error('attempted to get entity with bad id'),
        {
          id: id || 'NOT-PRESENT',
        }
      );

      return of(null);
    }
  }

  saveEntity(entity: any): Observable<any> {
    const entityId = entity.id;
    const isNew = !entityId;
    const url: string = entityId
      ? `${this.entitiesUrl}/${entityId}`
      : this.entitiesUrl;
    const serializedEntity = serialize(entity);

    let method: string;
    let data: any;

    if (isNew) {
      method = 'post';
      data = serializedEntity
        .remove(this.model.KEYS.ID)
        .remove(this.model.KEYS.REVISION_ID)
        .toJS();
    } else {
      method = 'put';
      data = serializedEntity.toJS();
    }

    return this.authHttpService[method](url, data, this.inputHeaders).pipe(
      map(({ body, ..._ }: IResponseInfo): any => {
        const parentKey = isNew
          ? this.topLevelKeys.postKey
          : this.topLevelKeys.putKey;
        const e = body[parentKey];

        e[IS_NEW_KEY] = isNew;

        return new this.model(e);
      }),
      take(1)
    );
  }

  deleteEntity(entity: any): Observable<IResponseInfo> {
    const url = Location.joinWithSlash(this.entitiesUrl, entity.id);
    const data = {
      local_id: entity.localId,
      revision_id: entity.revisionId,
    };

    if (!entity.localId) {
      this.errorService.alertSystem(
        new Error('entity local id is empty while trying to delete'),
        {
          entity,
        }
      );
    }

    this.loggerService.log(
      'deleting entity:',
      data,
      'from network @ url:',
      url
    );

    return this.authHttpService
      .delete(url, data, this.inputHeaders)
      .pipe(take(1));
  }
}
